import React from "react";
import {
  describeLiveViewFailure,
  describeLiveViewFailureCompact,
  LiveViewFailure,
  TRY_AGAIN_LABEL,
  useIsLiveViewStaff,
} from "./AlertsUtils/liveViewFailure";
import type { WhepLiveStreamStatus } from "./AlertsUtils/useWhepLiveStream";

interface LiveViewFailureNoticeProps {
  status: WhepLiveStreamStatus;
  failure?: LiveViewFailure | null;
  /** Re-run play(). The button shows only for retryable failures. */
  onRetry?: () => void;
  /**
   * "player": the single Live View (large copy + button).
   * "tile": a non-compact wall tile (spotlight / full dialog).
   * "compact": a small wall tile — short copy, no button.
   */
  variant?: "player" | "tile" | "compact";
}

const STYLES = {
  player: {
    message: "font-mulish text-center text-[18px] text-white",
    detail:
      "mt-2 text-center font-mono text-[11px] leading-snug text-white/50 break-all",
    button:
      "font-mulish mt-4 rounded bg-[#FF8400] px-4 py-2 text-[14px] font-bold text-white hover:bg-[#e67700]",
  },
  tile: {
    message:
      "font-mulish px-4 text-center text-xl font-semibold leading-snug text-white/90",
    detail:
      "mt-1 px-4 text-center font-mono text-[11px] leading-snug text-white/50 break-all",
    button:
      "pointer-events-auto font-mulish mt-3 rounded bg-[#FF8400] px-3 py-1.5 text-[13px] font-bold text-white hover:bg-[#e67700]",
  },
  compact: {
    message: "font-mulish text-center text-[9px] leading-tight text-white/80",
    detail:
      "mt-0.5 text-center font-mono text-[7px] leading-tight text-white/50 break-all",
    button: "",
  },
} as const;

/**
 * Plain-words reason a live view failed, with a small grey technical line for
 * staff (support acts on screenshots) and "Try again" when a retry can help.
 * Shared by the Live View player and the Site Video Wall tiles.
 */
export const LiveViewFailureNotice: React.FC<LiveViewFailureNoticeProps> = ({
  status,
  failure,
  onRetry,
  variant = "player",
}) => {
  const staff = useIsLiveViewStaff();
  const describe =
    variant === "compact"
      ? describeLiveViewFailureCompact
      : describeLiveViewFailure;
  const { message, detail, retryable } = describe(status, failure, { staff });
  const styles = STYLES[variant];
  const showRetry = retryable && Boolean(onRetry) && variant !== "compact";

  return (
    <>
      <p className={styles.message} data-testid="live-view-failure-message">
        {message}
      </p>
      {detail && (
        <p className={styles.detail} data-testid="live-view-failure-detail">
          {detail}
        </p>
      )}
      {showRetry && (
        <button
          type="button"
          onClick={(event) => {
            // Wall tiles are clickable (focus); a retry must not also focus.
            event.stopPropagation();
            onRetry?.();
          }}
          className={styles.button}
        >
          {TRY_AGAIN_LABEL}
        </button>
      )}
    </>
  );
};
